/**
 * @fileoverview Who are you teaching? The recorder's picker (iOS review B6).
 *
 * Today's sessions sit at the top, so the Scheduled today list no longer
 * lives in the capture screen, and everyone else follows A to Z. A student
 * without consent says so in the row; the card on the recorder then shows
 * the same state. The web twin of `mobile/src/components/RecordingPickerSheet.js`.
 */
import { useMemo, useState } from "react";
import { Users } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { groupStudentsIntoSections } from "@/lib/studentSort";
import { consentStateLabel } from "@/lib/consent";
import { occurrenceKey, sameSelection } from "@/lib/schedulingView";
import { formatTime12h } from "@/lib/schedule";

const displayName = (s) => `${s?.first_name || ""} ${s?.last_name || ""}`.trim();
const initialOf = (s) => (displayName(s) || "?").charAt(0).toUpperCase();

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {Array} props.students - The roster.
 * @param {string|null} [props.selectedStudentId]
 * @param {(student: Object) => void} props.onPickStudent
 * @param {Array} [props.today] - Today's occurrences, when scheduling is on.
 * @param {Object|null} [props.selection] - The current scheduler selection, to mark the chosen row.
 * @param {(occurrence: Object) => void} [props.onPickOccurrence]
 * @param {boolean} [props.showContext] - Whether to name the business on a session row.
 */
export function RecordingPickerDialog({
  open,
  onOpenChange,
  students,
  selectedStudentId = null,
  onPickStudent,
  today = [],
  selection = null,
  onPickOccurrence,
  showContext = false,
}) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => displayName(s).toLowerCase().includes(q));
  }, [students, query]);
  const sections = useMemo(() => groupStudentsIntoSections(filtered), [filtered]);
  const searching = query.trim().length > 0;

  const close = (next) => {
    if (!next) setQuery("");
    onOpenChange(next);
  };
  const pickStudent = (student) => {
    setQuery("");
    onPickStudent(student);
  };
  const pickOccurrence = (occ) => {
    setQuery("");
    onPickOccurrence?.(occ);
  };

  const rowClass = (picked) =>
    [
      "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
      picked
        ? "border-[var(--accent-yellow-border-strong)] bg-[var(--accent-yellow-faint)]"
        : "border-transparent hover:bg-[var(--studio-interactive)]",
    ].join(" ");

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-h-[86vh] overflow-hidden flex flex-col gap-0 bg-[var(--studio-overlay)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] p-0"
        data-testid="recording-picker"
      >
        <DialogHeader className="px-5 pt-5 pb-3 text-left">
          <DialogTitle className="font-serif text-xl font-normal text-[var(--text-primary)]">Who are you teaching?</DialogTitle>
          <DialogDescription className="sr-only">Your students, and today’s lessons.</DialogDescription>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            aria-label="Search students"
            autoComplete="off"
            className="mt-2 h-10 w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3.5 text-[15px] text-[var(--text-primary)] placeholder:text-[var(--text-placeholder)] focus:outline-hidden focus:border-[var(--accent-yellow-border-strong)]"
            data-testid="picker-search"
          />
        </DialogHeader>

        <div className="overflow-y-auto px-3 pb-5">
          {!searching && today.length > 0 && (
            <div data-testid="picker-today" className="mb-2">
              <p className="px-2 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]">Today</p>
              <ul className="space-y-1.5">
                {today.map((occ) => {
                  const picked = sameSelection(selection, occ);
                  const recorded = !!(occ.lesson_id || occ.teaching_confirmed);
                  const isClass = !!occ.class_id;
                  const sub = [
                    formatTime12h(occ.time_local),
                    showContext ? occ.context_label || "Unassigned" : null,
                    isClass && occ.student_count ? `${occ.student_count} students` : null,
                  ].filter(Boolean).join(" · ");
                  return (
                    <li key={occurrenceKey(occ)}>
                      <button
                        type="button"
                        onClick={() => pickOccurrence(occ)}
                        aria-pressed={picked}
                        data-picked={picked ? "true" : "false"}
                        className={`${rowClass(picked)} border-[var(--studio-divider)] bg-[var(--studio-raised)]`}
                        data-testid={`scheduled-pick-${occ.scheduler_appointment_id || occ.slot_id}`}
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--studio-interactive)] text-sm font-semibold text-[var(--accent-yellow-text)]">
                          {isClass ? <Users className="h-4 w-4" aria-hidden="true" /> : (occ.display_name || "L").trim().charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[15px] text-[var(--text-primary)]">{occ.display_name || "Lesson"}</span>
                          {sub && <span className="block truncate text-xs text-[var(--text-tertiary)]">{sub}</span>}
                        </span>
                        <span className="shrink-0 text-xs text-[var(--text-tertiary)]">
                          {recorded ? "Recorded" : picked ? "Chosen" : ""}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <p className="px-2 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]">Everyone</p>
            </div>
          )}

          {students.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-[var(--text-tertiary)]">No students yet</p>
          ) : sections.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-[var(--text-tertiary)]">Nobody matches “{query.trim()}”</p>
          ) : (
            sections.map((section) => (
              <div key={section.letter}>
                <p className="px-2 pt-3 pb-1 text-xs font-semibold text-[var(--accent-yellow-text)]" data-testid={`section-header-${section.letter}`}>
                  {section.title}
                </p>
                <ul>
                  {section.data.map((student) => {
                    const picked = selectedStudentId === student.student_id;
                    return (
                      <li key={student.student_id}>
                        <button
                          type="button"
                          onClick={() => pickStudent(student)}
                          aria-pressed={picked}
                          data-picked={picked ? "true" : "false"}
                          className={rowClass(picked)}
                          data-testid={`pick-student-${student.student_id}`}
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--studio-interactive)] text-sm font-semibold text-[var(--accent-yellow-text)]">
                            {initialOf(student)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[15px] text-[var(--text-primary)]">{displayName(student)}</span>
                            {student.consent_status !== "granted" && (
                              <span className="block truncate text-xs text-[var(--text-tertiary)]">{consentStateLabel(student)}</span>
                            )}
                          </span>
                          <span className="max-w-[120px] shrink-0 truncate text-xs text-[var(--text-tertiary)]">
                            {(student.instruments || []).join(", ")}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RecordingPickerDialog;
